/**
 * Analytics Service
 * Centralized Amplitude wrapper for product usage tracking.
 * Only sends events in production when AMPLITUDE_API_KEY is configured.
 */

import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

type EventProperties = Record<string, string | number | boolean | undefined>;

export type UserContext = {
    userId: string;
    email?: string;
    name?: string;
    role?: string;
    companyId?: string;
    companyName?: string;
};

export type PageViewContext = {
    routeName: string;
    path: string;
    pageTitle?: string;
    previousRouteName?: string;
    isAuthenticated?: boolean;
    routeParams?: Record<string, unknown>;
};

class AnalyticsService {
    private initialized = false;
    private lastContextKey: string | null = null;
    private lastPageViewKey: string | null = null;
    private sessionReplayPlugin: ReturnType<typeof sessionReplayPlugin> | null = null;

    init(environment: string): void {
        if (environment !== 'production') {
            return;
        }

        const apiKey = process.env.AMPLITUDE_API_KEY;
        if (!apiKey) {
            return;
        }

        const sessionReplaySampleRate = process.env.AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE;
        const parsedSampleRate = sessionReplaySampleRate !== undefined && sessionReplaySampleRate !== ''
            ? Number.parseFloat(sessionReplaySampleRate)
            : undefined;
        const serverZone = process.env.AMPLITUDE_SERVER_ZONE === 'EU' ? 'EU' : 'US';
        const debugMode = process.env.AMPLITUDE_SESSION_REPLAY_DEBUG === 'true';

        this.sessionReplayPlugin = sessionReplayPlugin({
            forceSessionTracking: true,
            enableUrlChangePolling: true,
            useWebWorker: true,
            shouldInlineStylesheet: true,
            debugMode,
            privacyConfig: {
                defaultMaskLevel: 'medium'
            },
            performanceConfig: {
                enabled: true
            },
            ...(parsedSampleRate !== undefined && !Number.isNaN(parsedSampleRate)
                ? { sampleRate: parsedSampleRate }
                : {})
        });

        amplitude.add(this.sessionReplayPlugin);
        amplitude.init(apiKey, {
            serverZone,
            autocapture: {
                sessions: true,
                pageViews: false,
                formInteractions: false,
                fileDownloads: false,
                attribution: false,
                elementInteractions: false,
                networkTracking: false
            }
        });
        this.initialized = true;
    }

    track(eventName: string, properties?: EventProperties): void {
        if (!this.initialized) {
            return;
        }

        amplitude.track(eventName, properties);
    }

    /** Legacy GA-style API used during Angular → Amplitude migration */
    trackEvent(category: string, action?: string, label?: string): void {
        const eventName = action ? `${category} ${action}` : category;
        this.track(eventName, {
            category,
            action,
            label
        });
    }

    /**
     * Canonical feature-usage tracking. Preferred API for new instrumentation.
     *
     * Emits a Title-Case event name built from `feature` + `action` (e.g.
     * `trackFeature('actions', 'Plan Created')` → `"Actions Plan Created"`) and
     * always attaches the standard properties `{ feature, action, result }` plus
     * any non-sensitive context props. This is what lets Amplitude answer which
     * features are used, how much, and how.
     *
     * Do NOT pass free-text user content or PII in `props` — only enum-like,
     * low-cardinality context (ids, types, counts, statuses).
     *
     * See `analytics-events.md` for the event catalog and naming convention.
     */
    trackFeature(
        feature: string,
        action: string,
        props?: EventProperties,
        result: 'success' | 'fail' = 'success'
    ): void {
        const toTitleCase = (value: string): string =>
            value
                .split(/[\s_]+/)
                .filter(Boolean)
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

        const eventName = `${toTitleCase(feature)} ${action}`.trim();

        this.track(eventName, {
            feature,
            action,
            result,
            ...props
        });
    }

    trackPageView(context: PageViewContext): void {
        if (!this.initialized || !context.routeName || context.routeName === '') {
            return;
        }

        const pageViewKey = JSON.stringify({
            routeName: context.routeName,
            path: context.path,
            routeParams: context.routeParams || {}
        });
        if (pageViewKey === this.lastPageViewKey) {
            return;
        }
        this.lastPageViewKey = pageViewKey;

        const routeSection = context.routeName.split('.')[0];

        this.track('Page Viewed', {
            route_name: context.routeName,
            route_section: routeSection,
            path: context.path,
            page_title: context.pageTitle,
            previous_route_name: context.previousRouteName,
            is_authenticated: context.isAuthenticated
        });
    }

    identifyUserContext(context: UserContext, options?: { flush?: boolean }): void {
        if (!this.initialized || !context.userId) {
            return;
        }

        const contextKey = JSON.stringify(context);
        if (contextKey === this.lastContextKey) {
            return;
        }
        this.lastContextKey = contextKey;

        amplitude.setUserId(String(context.userId));

        const identify = new Identify();
        let hasUserProperties = false;

        const setUserProperty = (key: string, value?: string) => {
            if (value) {
                identify.set(key, value);
                hasUserProperties = true;
            }
        };

        setUserProperty('email', context.email);
        setUserProperty('name', context.name);
        setUserProperty('role', context.role);
        setUserProperty('company_id', context.companyId);
        setUserProperty('company_name', context.companyName);

        if (hasUserProperties) {
            amplitude.identify(identify);
        }

        if (context.companyId) {
            amplitude.setGroup('company', String(context.companyId));

            if (context.companyName) {
                const groupIdentify = new Identify();
                groupIdentify.set('company_name', context.companyName);
                amplitude.groupIdentify('company', String(context.companyId), groupIdentify);
            }
        }

        if (options?.flush !== false) {
            amplitude.flush();
        }
    }

    clearUserContextCache(): void {
        this.lastContextKey = null;
        this.lastPageViewKey = null;
    }

    reset(): void {
        if (!this.initialized) {
            return;
        }

        this.lastContextKey = null;
        this.lastPageViewKey = null;
        amplitude.reset();
    }
}

export const analyticsService = new AnalyticsService();

export default AnalyticsService;
